import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Modal from '../components/common/Modal';
import DocumentViewer from '../components/common/DocumentViewer';
import DatePicker from '../components/common/DatePicker';
import AbaProtegida from '../components/common/AbaProtegida';
import { SolicitacoesService } from '../services/solicitacoesService';
import { PedidosService } from '../services/pedidosService';
import CargasService from '../services/cargasServiceAPI';
import { AuditService } from '../services/auditService';
import { ValidationService } from '../services/validationService';
import { UploadService } from '../services/uploadService';
import NotificacoesService from '../services/notificacoesService';
import { StatusSolicitacao, TipoAcaoAudit, SolicitacaoAgendamento } from '../types';
import { usePermissoes } from '../hooks/usePermissoes';

const GestaoAgendamento: React.FC = () => {
  const { user } = useAuth();
  const { filialAtual } = useApp();
  const { temPermissao: podeAprovar } = usePermissoes('gestao_agendamento', 'aprovar_solicitacao_agendamento');
  const { temPermissao: podeReprovar } = usePermissoes('gestao_agendamento', 'reprovar_solicitacao_agendamento');
  const { temPermissao: podeReagendar } = usePermissoes('gestao_agendamento', 'reagendar_solicitacao');
  const [solicitacoes, setSolicitacoes] = useState<SolicitacaoAgendamento[]>([]);
  const [filteredSolicitacoes, setFilteredSolicitacoes] = useState<SolicitacaoAgendamento[]>([]);
  const [loading, setLoading] = useState(false);
  const [pedidos, setPedidos] = useState<any[]>([]);
  const [filters, setFilters] = useState({
    status: '',
    fornecedor: ''
  });
  const [modalReagendamento, setModalReagendamento] = useState<{ open: boolean; solicitacao?: SolicitacaoAgendamento }>({ open: false });
  const [formDataReagendamento, setFormDataReagendamento] = useState({ novaData: '', motivo: '' });
  const [modalDocumentos, setModalDocumentos] = useState<{ open: boolean; solicitacao?: SolicitacaoAgendamento; documentos?: any[] }>({ open: false });
  const [abaAtiva, setAbaAtiva] = useState<'pendentes' | 'aguardando_pedido' | 'pedido_digitado' | 'aprovados' | 'reprovados' | 'reagendados' | 'todas'>('pendentes');
  const [dataFiltroHistorico, setDataFiltroHistorico] = useState('');
  const [fornecedorFiltro, setFornecedorFiltro] = useState('');
  const [dataInicialFiltro, setDataInicialFiltro] = useState('');
  const [dataFinalFiltro, setDataFinalFiltro] = useState('');
  const [historicoAprovacoes, setHistoricoAprovacoes] = useState<any[]>([]);
  const [historicoReprovacoes, setHistoricoReprovacoes] = useState<any[]>([]);
  const [historicoReagendamentos, setHistoricoReagendamentos] = useState<any[]>([]);
  const [loadingHistorico, setLoadingHistorico] = useState(false);
  const [solicitandoPedido, setSolicitandoPedido] = useState(false);
  const [contagens, setContagens] = useState({
    pendentes: 0,
    aguardando_pedido: 0,
    pedido_digitado: 0,
    aprovados: 0,
    reprovados: 0,
    reagendados: 0
  });

  useEffect(() => {
    carregarSolicitacoes();
    carregarContagensTotais();
  }, [filialAtual, abaAtiva]);

  // Carregar contagens totais de todas as solicitações (independente da aba ativa)
  const carregarContagensTotais = async () => {
    try {
      const todasSolicitacoes = await SolicitacoesService.buscarTodasSolicitacoes(filialAtual || undefined);
      
      // Buscar pedidos para verificar quais já foram digitados
      const pedidosService = PedidosService.getInstance();
      const { data: todosPedidos } = await pedidosService.buscarPedidos({ filial: filialAtual || undefined });

      // Filtrar solicitações aguardando pedido para excluir as com pedido já digitado
      const aguardandoPedidoFiltrado = todasSolicitacoes.filter(solicitacao => {
        if (solicitacao.status !== StatusSolicitacao.AGUARDANDO_PEDIDO) return false;
        
        if (!solicitacao.pedido_compra_id) return true; // Sem pedido, deve contar

        const pedidoVinculado = todosPedidos?.find((p: any) => p.id === solicitacao.pedido_compra_id);
        // Se o pedido não tem numero_pedido e não tem arquivo_id, ainda não foi digitado
        return !pedidoVinculado?.numero_pedido && !pedidoVinculado?.arquivo_id;
      });

      setContagens({
        pendentes: todasSolicitacoes.filter(s => s.status === StatusSolicitacao.PENDENTE).length,
        aguardando_pedido: aguardandoPedidoFiltrado.length,
        pedido_digitado: todasSolicitacoes.filter(s => s.status === StatusSolicitacao.PEDIDO_DIGITADO).length,
        aprovados: todasSolicitacoes.filter(s => s.status === StatusSolicitacao.APROVADO).length,
        reprovados: todasSolicitacoes.filter(s => s.status === StatusSolicitacao.REPROVADO).length,
        reagendados: todasSolicitacoes.filter(s => s.status === StatusSolicitacao.REAGENDADO).length
      });
    } catch (error) {
      console.error('Erro ao carregar contagens:', error);
    }
  };

  // Calcular contagens de solicitações por status
  useEffect(() => {
    carregarContagensTotais();
  }, [filialAtual]);

  useEffect(() => {
    // Aplicar filtros
    let filtered = solicitacoes;

    if (filters.status) {
      filtered = filtered.filter(s => s.status === filters.status);
    }

    if (filters.fornecedor) {
      filtered = filtered.filter(s => 
        s.fornecedor?.toLowerCase().includes(filters.fornecedor.toLowerCase())
      );
    }

    setFilteredSolicitacoes(filtered);
  }, [filters, solicitacoes]);

  const carregarSolicitacoes = async () => {
    try {
      setLoading(true);
      
      let dados: SolicitacaoAgendamento[] = [];
      
      switch (abaAtiva) {
        case 'pendentes':
          dados = await SolicitacoesService.buscarPendentes(filialAtual || undefined);
          break;
        case 'aguardando_pedido':
          dados = await SolicitacoesService.buscarAguardandoPedido(filialAtual || undefined);
          break;
        case 'pedido_digitado':
          dados = await SolicitacoesService.buscarPedidoDigitado(filialAtual || undefined);
          // Carregar pedidos vinculados
          const pedidosService = PedidosService.getInstance();
          const pedidosResult = await pedidosService.buscarPedidos({ filial: filialAtual || undefined });
          if (pedidosResult.data) {
            setPedidos(pedidosResult.data);
          }
          break;
        case 'aprovados':
          dados = await SolicitacoesService.buscarPorStatus(StatusSolicitacao.APROVADO, filialAtual || undefined);
          break;
        case 'reprovados':
          dados = await SolicitacoesService.buscarPorStatus(StatusSolicitacao.REPROVADO, filialAtual || undefined);
          break;
        case 'reagendados':
          dados = await SolicitacoesService.buscarPorStatus(StatusSolicitacao.REAGENDADO, filialAtual || undefined);
          break;
        case 'todas':
          dados = await SolicitacoesService.buscarTodasSolicitacoes(filialAtual || undefined);
          break;
        default:
          dados = [];
      }
      
      setSolicitacoes(dados);
    } catch (error) {
      console.error('Erro ao carregar solicitações:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVisualizarDocumentos = (solicitacao: SolicitacaoAgendamento) => {
    // Buscar pedido vinculado usando solicitacao_id (não pedido_compra_id)
    const pedidoVinculado = pedidos.find(p => p.solicitacao_id === solicitacao.id);
    
    // Combinar documentos originais com documentos do pedido
    const documentosOriginais = solicitacao.documentos || [];
    const documentosPedido = pedidoVinculado?.documentos_digitados || [];
    
    // Criar array combinado de documentos
    const todosDocumentos = [
      ...documentosOriginais,
      ...documentosPedido.map((doc: any) => ({
        nome: doc.nome,
        tipo: doc.tipo,
        databaseKey: doc.databaseKey,
        url: doc.url,
        tamanho: doc.tamanho || 0,
        uploadedAt: doc.uploadedAt,
        fornecedorMercadoria: doc.fornecedorMercadoria
      }))
    ];
    
    setModalDocumentos({ open: true, solicitacao, documentos: todosDocumentos });
  };

  // Filtrar solicitações de histórico por fornecedor e intervalo de datas
  const filtrarHistorico = (solicitacoes: SolicitacaoAgendamento[], status?: StatusSolicitacao): SolicitacaoAgendamento[] => {
    let filtradas = solicitacoes;

    // Filtrar por status se fornecido
    if (status) {
      filtradas = filtradas.filter(s => s.status === status);
    }

    if (fornecedorFiltro) {
      filtradas = filtradas.filter(s => 
        s.fornecedor?.toLowerCase().includes(fornecedorFiltro.toLowerCase())
      );
    }

    if (dataInicialFiltro) {
      const dataInicio = new Date(dataInicialFiltro);
      filtradas = filtradas.filter(s => 
        new Date(s.data_atualizacao) >= dataInicio
      );
    }

    if (dataFinalFiltro) {
      const dataFim = new Date(dataFinalFiltro);
      filtradas = filtradas.filter(s => 
        new Date(s.data_atualizacao) <= dataFim
      );
    }

    return filtradas;
  };

  const handleAprovar = async (solicitacao: SolicitacaoAgendamento) => {
    try {
      // Validar aprovação
      const validacao = ValidationService.validarAprovacao(solicitacao);
      
      if (!validacao.isValid) {
        const mensagem = ValidationService.formatarMensagens(validacao);
        alert(`Não é possível aprovar esta solicitação:\n\n${mensagem}`);
        return;
      }
      
      // Mostrar avisos se houver
      if (validacao.warnings && validacao.warnings.length > 0) {
        const avisos = validacao.warnings.join('\n  - ');
        const confirmar = window.confirm(`Atenção:\n  - ${avisos}\n\nDeseja continuar com a aprovação?`);
        if (!confirmar) return;
      }
      
      await SolicitacoesService.aprovarSolicitacao(solicitacao.id);
      
      // Enviar notificação ao solicitante
      if (solicitacao.solicitante_id) {
        await NotificacoesService.notificarSolicitacaoAprovada(
          solicitacao.solicitante_id,
          solicitacao.id,
          solicitacao.fornecedor,
          solicitacao.tipo_produto || ''
        );
      }
      
      await AuditService.registrarAcao({
        usuarioId: user?.id || '',
        usuarioNome: user?.nome || '',
        acao: TipoAcaoAudit.ATUALIZAR,
        tabela: 'solicitacoes_agendamento',
        registroId: solicitacao.id,
        valoresAntigos: { status: solicitacao.status },
        valoresNovos: { status: StatusSolicitacao.AGUARDANDO_PEDIDO }
      });
      carregarSolicitacoes();
    } catch (error) {
      console.error('Erro ao aprovar solicitação:', error);
      alert('Erro ao aprovar solicitação!');
    }
  };

  const handleReprovar = async (solicitacao: SolicitacaoAgendamento) => {
    const motivo = prompt('Motivo da reprovação:');
    if (!motivo) return;
    
    try {
      // Validar reprovação
      const validacao = ValidationService.validarReprovacao(solicitacao, motivo);
      
      if (!validacao.isValid) {
        const mensagem = ValidationService.formatarMensagens(validacao);
        alert(`Não é possível reprovar esta solicitação:\n\n${mensagem}`);
        return;
      }
      
      await SolicitacoesService.reprovar(solicitacao.id, motivo);
      
      // Enviar notificação ao solicitante (não bloqueia se falhar)
      if (solicitacao.solicitante_id) {
        NotificacoesService.notificarSolicitacaoReprovada(
          solicitacao.solicitante_id,
          solicitacao.id,
          solicitacao.fornecedor,
          motivo
        ).catch(err => console.warn('Notificação não enviada (não crítico):', err));
      }
      
      // Registrar auditoria (não bloqueia se falhar)
      AuditService.registrarAcao({
        usuarioId: user?.id || '',
        usuarioNome: user?.nome || '',
        acao: TipoAcaoAudit.ATUALIZAR,
        tabela: 'solicitacoes_agendamento',
        registroId: solicitacao.id,
        valoresAntigos: { status: solicitacao.status },
        valoresNovos: { status: StatusSolicitacao.REPROVADO, motivo_reprovacao: motivo }
      }).catch(err => console.warn('Auditoria não registrada (não crítico):', err));
      
      carregarSolicitacoes();
    } catch (error) {
      console.error('Erro ao reprovar solicitação:', error);
      alert('Erro ao reprovar solicitação!');
    }
  };

  const handleReagendar = async () => {
    if (!modalReagendamento.solicitacao || !formDataReagendamento.novaData || !formDataReagendamento.motivo) {
      alert('Preencha todos os campos!');
      return;
    }

    try {
      // Adicionar timezone à nova data
      const novaDataComTimezone = formDataReagendamento.novaData + 'T00:00:00-03:00';

      // Validar reagendamento
      const validacao = ValidationService.validarReagendamento(
        modalReagendamento.solicitacao,
        novaDataComTimezone,
        formDataReagendamento.motivo
      );
      
      if (!validacao.isValid) {
        const mensagem = ValidationService.formatarMensagens(validacao);
        alert(`Não é possível reagendar esta solicitação:\n\n${mensagem}`);
        return;
      }
      
      // Mostrar avisos se houver
      if (validacao.warnings && validacao.warnings.length > 0) {
        const avisos = validacao.warnings.join('\n  - ');
        const confirmar = window.confirm(`Atenção:\n  - ${avisos}\n\nDeseja continuar com o reagendamento?`);
        if (!confirmar) return;
      }

      await SolicitacoesService.proporReagendamento(
        modalReagendamento.solicitacao.id,
        novaDataComTimezone,
        formDataReagendamento.motivo,
        user?.id || ''
      );

      // Enviar notificação ao solicitante
      if (modalReagendamento.solicitacao.solicitante_id) {
        await NotificacoesService.notificarSolicitacaoReagendada(
          modalReagendamento.solicitacao.solicitante_id,
          modalReagendamento.solicitacao.id,
          modalReagendamento.solicitacao.fornecedor,
          novaDataComTimezone
        );
      }

      await AuditService.registrarAcao({
        usuarioId: user?.id || '',
        usuarioNome: user?.nome || '',
        acao: TipoAcaoAudit.ATUALIZAR,
        tabela: 'solicitacoes_agendamento',
        registroId: modalReagendamento.solicitacao.id,
        valoresAntigos: { data_desejada: modalReagendamento.solicitacao.data_desejada },
        valoresNovos: { data_desejada: novaDataComTimezone }
      });

      setModalReagendamento({ open: false });
      setFormDataReagendamento({ novaData: '', motivo: '' });
      carregarSolicitacoes();
    } catch (error) {
      console.error('Erro ao reagendar solicitação:', error);
      alert('Erro ao reagendar solicitação!');
    }
  };

  const handleAbrirModalReagendamento = (solicitacao: SolicitacaoAgendamento) => {
    setModalReagendamento({ open: true, solicitacao });
    setFormDataReagendamento({ novaData: '', motivo: '' });
  };

  const handleSolicitarPedido = async (solicitacao: SolicitacaoAgendamento) => {
    if (solicitandoPedido) return;
    
    try {
      setSolicitandoPedido(true);
      
      // Validar solicitação de pedido
      const validacao = ValidationService.validarSolicitacaoPedido(solicitacao);
      
      if (!validacao.isValid) {
        const mensagem = ValidationService.formatarMensagens(validacao);
        alert(`Não é possível solicitar pedido para esta solicitação:\n\n${mensagem}`);
        setSolicitandoPedido(false);
        return;
      }
      
      // Mostrar avisos se houver
      if (validacao.warnings && validacao.warnings.length > 0) {
        const avisos = validacao.warnings.join('\n  - ');
        const confirmar = window.confirm(`Atenção:\n  - ${avisos}\n\nDeseja continuar com a solicitação do pedido?`);
        if (!confirmar) {
          setSolicitandoPedido(false);
          return;
        }
      }
      
      // Usar o novo método solicitarPedidoCompra
      const resultado = await SolicitacoesService.solicitarPedidoCompra(
        solicitacao.id,
        user?.id || ''
      );

      if (resultado.error) {
        throw resultado.error;
      }

      // Enviar notificação ao solicitante (não bloqueia se falhar)
      if (solicitacao.solicitante_id) {
        const numeroPedido = (resultado.data as any)?.numero_pedido || 'N/A';
        NotificacoesService.notificarPedidoCriado(
          solicitacao.solicitante_id,
          solicitacao.id,
          numeroPedido
        ).catch(err => console.warn('Notificação não enviada (não crítico):', err));
      }

      // Registrar no audit trail
      await AuditService.registrarAcao({
        usuarioId: user?.id || '',
        usuarioNome: user?.nome || '',
        acao: TipoAcaoAudit.ATUALIZAR,
        tabela: 'solicitacoes_agendamento',
        registroId: solicitacao.id,
        valoresAntigos: { status: solicitacao.status },
        valoresNovos: { status: StatusSolicitacao.AGUARDANDO_PEDIDO }
      });

      alert('Pedido solicitado com sucesso!');
      carregarSolicitacoes(); // Recarrega para mostrar na aba correta
    } catch (error) {
      console.error('Erro ao solicitar pedido:', error);
      alert('Erro ao solicitar pedido!');
    } finally {
      setSolicitandoPedido(false);
    }
  };

  const getStatusColor = (status: StatusSolicitacao) => {
    switch (status) {
      case StatusSolicitacao.PENDENTE: return 'bg-blue-100 text-blue-800';
      case StatusSolicitacao.REPROVADO: return 'bg-red-100 text-red-800';
      case StatusSolicitacao.REAGENDADO: return 'bg-orange-100 text-orange-800';
      case StatusSolicitacao.AGUARDANDO_PEDIDO: return 'bg-blue-100 text-blue-800';
      case StatusSolicitacao.PEDIDO_DIGITADO: return 'bg-purple-100 text-purple-800';
      case StatusSolicitacao.APROVADO: return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPrioridadeBadge = (prioridade?: number) => {
    if (!prioridade) return null;
    
    if (prioridade >= 5) return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Crítico</span>;
    if (prioridade >= 4) return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">Alta</span>;
    if (prioridade >= 3) return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Média</span>;
    if (prioridade >= 2) return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Baixa</span>;
    return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">Normal</span>;
  };

  const getIconeTipoArquivo = (tipo: string) => {
    if (tipo.includes('pdf')) return 'PDF';
    if (tipo.includes('xml')) return 'XML';
    if (tipo.includes('image')) return 'IMG';
    if (tipo.includes('word')) return 'DOC';
    return 'FILE';
  };

  const formatarData = (dataString: string) => {
    if (!dataString) return '';
    // Converter string para Date usando fuso horário local para evitar problemas de UTC
    if (dataString.includes('T')) {
      const [ano, mes, dia] = dataString.split('T')[0].split('-').map(Number);
      const data = new Date(ano, mes - 1, dia);
      return data.toLocaleDateString('pt-BR');
    } else {
      const [ano, mes, dia] = dataString.split('-').map(Number);
      const data = new Date(ano, mes - 1, dia);
      return data.toLocaleDateString('pt-BR');
    }
  };

  const formatarDataHora = (dataString: string) => {
    if (!dataString) return '';
    const data = new Date(dataString);
    return data.toLocaleString('pt-BR');
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Gestão de Agendamento</h1>
        
        {/* Abas de navegação */}
        <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 overflow-x-auto">
          <button
            onClick={() => setAbaAtiva('pendentes')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors relative ${
              abaAtiva === 'pendentes'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Solicitações Pendentes
            {contagens.pendentes > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {contagens.pendentes}
              </span>
            )}
          </button>
          <button
            onClick={() => setAbaAtiva('aguardando_pedido')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors relative ${
              abaAtiva === 'aguardando_pedido'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Aguardando Pedido
            {contagens.aguardando_pedido > 0 && (
              <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {contagens.aguardando_pedido}
              </span>
            )}
          </button>
          <button
            onClick={() => setAbaAtiva('pedido_digitado')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors relative ${
              abaAtiva === 'pedido_digitado'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Pedido Digitado
            {contagens.pedido_digitado > 0 && (
              <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {contagens.pedido_digitado}
              </span>
            )}
          </button>
          <button
            onClick={() => setAbaAtiva('aprovados')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              abaAtiva === 'aprovados'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Aprovações
          </button>
          <button
            onClick={() => setAbaAtiva('reprovados')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              abaAtiva === 'reprovados'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Reprovações
          </button>
          <button
            onClick={() => setAbaAtiva('reagendados')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              abaAtiva === 'reagendados'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Reagendamentos
          </button>
          <button
            onClick={() => setAbaAtiva('todas')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              abaAtiva === 'todas'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Todas as Solicitações
          </button>
        </div>
      </div>

      {/* Conteúdo da aba Solicitações Pendentes */}
      {abaAtiva === 'pendentes' && (
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Carregando solicitações...</p>
            </div>
          ) : solicitacoes.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Nenhuma solicitação pendente encontrada</p>
            </div>
          ) : (
            solicitacoes.map((solicitacao) => (
              <div 
                key={solicitacao.id} 
                className="bg-white rounded-lg shadow p-4 border-l-4"
                style={{ borderLeftColor: solicitacao.prioridade && solicitacao.prioridade >= 5 ? '#dc2626' : solicitacao.prioridade && solicitacao.prioridade >= 4 ? '#f97316' : '#3b82f6' }}
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {getPrioridadeBadge(solicitacao.prioridade)}
                      <h3 className="text-lg font-semibold text-gray-900">
                        {solicitacao.fornecedor}
                      </h3>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(solicitacao.status)}`}>
                        {solicitacao.status}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 text-sm text-gray-600">
                      <div><strong>Data Desejada:</strong> {formatarData(solicitacao.data_desejada)}</div>
                      <div><strong>Hora Solicitação:</strong> {new Date(solicitacao.data_solicitacao).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</div>
                      <div><strong>Peso:</strong> {solicitacao.peso} kg</div>
                      <div><strong>Volume:</strong> {solicitacao.volume}</div>
                      <div><strong>Conferente:</strong> {solicitacao.conferente}</div>
                      <div><strong>Tipo Produto:</strong> {solicitacao.tipo_produto}</div>
                      {solicitacao.mercadoria_a_enviar && (
                        <div><strong>Mercadoria:</strong> {solicitacao.mercadoria_a_enviar}</div>
                      )}
                      <div><strong>Tipo Carga:</strong> {solicitacao.tipo_carga}</div>
                      <div><strong>Solicitante:</strong> {solicitacao.solicitante_nome}</div>
                      <div><strong>Filial:</strong> {solicitacao.filial}</div>
                    </div>
                                      </div>
                  <div className="flex space-x-2">
                    {podeAprovar && (
                    <Button
                      variant="success"
                      size="sm"
                        onClick={() => {
                          alert('Botão Aprovar clicado! ID: ' + solicitacao.id);
                          handleAprovar(solicitacao);
                        }}
                      disabled={loading}
                    >
                      Aprovar
                    </Button>
                    )}
                    {podeReprovar && (
                    <Button
                      variant="error"
                      size="sm"
                      onClick={() => handleReprovar(solicitacao)}
                      disabled={loading}
                    >
                      Reprovar
                    </Button>
                    )}
                    {podeReagendar && (
                    <Button
                      variant="warning"
                      size="sm"
                      onClick={() => handleAbrirModalReagendamento(solicitacao)}
                      disabled={loading}
                    >
                      Reagendar
                    </Button>
                    )}
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleSolicitarPedido(solicitacao)}
                      disabled={loading || solicitandoPedido}
                    >
                      Solicitar Pedido
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleVisualizarDocumentos(solicitacao)}
                    >
                      Documentos
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Conteúdo da aba Aguardando Pedido */}
      {abaAtiva === 'aguardando_pedido' && (
        <div className="space-y-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4">Solicitações Aguardando Pedido</h3>
            <p className="text-gray-600 mb-4">
              Solicitações aprovadas que aguardam digitação do pedido.
            </p>
            {loading ? (
              <div className="text-center py-12">
                <p className="text-gray-500">Carregando solicitações...</p>
              </div>
            ) : (
              <div className="space-y-2">
                {solicitacoes.map(solicitacao => (
                  <div key={solicitacao.id} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium">{solicitacao.fornecedor}</h4>
                        <p className="text-sm text-gray-600">Data desejada: {formatarData(solicitacao.data_desejada)}</p>
                        <p className="text-sm text-gray-600">Peso: {solicitacao.peso.toLocaleString('pt-BR')} kg</p>
                        <p className="text-sm text-gray-600">Volume: {solicitacao.volume} m³</p>
                        {solicitacao.pedido_compra_id && (
                          <p className="text-sm text-gray-600">ID Pedido: {solicitacao.pedido_compra_id}</p>
                        )}
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleVisualizarDocumentos(solicitacao)}
                        >
                          Documentos
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {solicitacoes.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-gray-500">Nenhuma solicitação aguardando pedido encontrada</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Conteúdo da aba Pedido Digitado */}
      {abaAtiva === 'pedido_digitado' && (
        <div className="space-y-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4">Pedidos Já Digitados</h3>
            <p className="text-gray-600 mb-4">
              Solicitações cujos pedidos já foram digitados e aguardam aprovação final.
            </p>
            {loading ? (
              <div className="text-center py-12">
                <p className="text-gray-500">Carregando solicitações...</p>
              </div>
            ) : (
              <div className="space-y-2">
                {solicitacoes
                  .filter(s => s.status === StatusSolicitacao.PEDIDO_DIGITADO)
                  .map(solicitacao => {
                    const pedidoVinculado = pedidos.find(p => p.solicitacao_id === solicitacao.id);
                    return (
                      <div key={solicitacao.id} className="border rounded-lg p-4 hover:bg-gray-50">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-medium">{solicitacao.fornecedor}</h4>
                            {pedidoVinculado?.numero_pedido && (
                              <p className="text-sm text-blue-600 font-semibold">Nº Pedido: {pedidoVinculado.numero_pedido}</p>
                            )}
                            <p className="text-sm text-gray-600">Data desejada: {formatarData(solicitacao.data_desejada)}</p>
                            <p className="text-sm text-gray-600">Peso: {solicitacao.peso.toLocaleString('pt-BR')} kg</p>
                            <p className="text-sm text-gray-600">Volume: {solicitacao.volume} m³</p>
                            <div className="mt-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleVisualizarDocumentos(solicitacao)}
                              >
                                Documentos
                              </Button>
                            </div>
                          </div>
                        <div className="flex space-x-2">
                          {podeAprovar && (
                          <Button
                            variant="success"
                            size="sm"
                            onClick={async () => {
                              try {
                                await CargasService.converterSolicitacaoEmCarga(solicitacao.id);
                                await AuditService.registrarAcao({
                                  usuarioId: user?.id || '',
                                  usuarioNome: user?.nome || '',
                                  acao: TipoAcaoAudit.ATUALIZAR,
                                  tabela: 'solicitacoes_agendamento',
                                  registroId: solicitacao.id,
                                  valoresAntigos: { status: solicitacao.status },
                                  valoresNovos: { status: StatusSolicitacao.APROVADO }
                                });
                                alert('Solicitação aprovada e convertida em carga com sucesso!');
                                carregarSolicitacoes();
                              } catch (error) {
                                console.error('Erro ao converter solicitação em carga:', error);
                                alert('Erro ao converter solicitação em carga!');
                              }
                            }}
                          >
                            Aprovar
                          </Button>
                          )}
                          {podeReprovar && (
                          <Button
                            variant="error"
                            size="sm"
                            onClick={() => {
                              const motivo = prompt('Motivo da reprovação:');
                              if (motivo) {
                                SolicitacoesService.reprovar(solicitacao.id, motivo);
                                carregarSolicitacoes();
                              }
                            }}
                          >
                            Reprovar
                          </Button>
                          )}
                          {podeReagendar && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setModalReagendamento({ open: true, solicitacao })}
                          >
                            Reagendar
                          </Button>
                          )}
                        </div>
                      </div>
                    </div>
                    );
                  })}
                {solicitacoes.filter(s => s.status === StatusSolicitacao.PEDIDO_DIGITADO).length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-gray-500">Nenhum pedido digitado encontrado</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Conteúdo da aba Aprovações */}
      {abaAtiva === 'aprovados' && (
        <div className="space-y-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4">Solicitações Aprovadas</h3>
            
            {/* Filtros */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fornecedor</label>
                <input
                  type="text"
                  placeholder="Buscar por fornecedor..."
                  value={fornecedorFiltro}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFornecedorFiltro(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data Inicial</label>
                <DatePicker
                  value={dataInicialFiltro}
                  onChange={(date) => setDataInicialFiltro(date ? date.toISOString().split('T')[0] : '')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data Final</label>
                <DatePicker
                  value={dataFinalFiltro}
                  onChange={(date) => setDataFinalFiltro(date ? date.toISOString().split('T')[0] : '')}
                />
              </div>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <p className="text-gray-500">Carregando solicitações...</p>
              </div>
            ) : filtrarHistorico(solicitacoes, StatusSolicitacao.APROVADO).length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">Nenhuma aprovação encontrada</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filtrarHistorico(solicitacoes, StatusSolicitacao.APROVADO).map((solicitacao) => (
                  <div 
                    key={solicitacao.id} 
                    className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-medium text-gray-900">{solicitacao.fornecedor}</h4>
                      <span className="text-sm text-gray-500">{solicitacao.solicitante_nome}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-sm text-gray-600 mb-2">
                      <div><strong>Data Desejada:</strong> {formatarData(solicitacao.data_desejada)}</div>
                      <div><strong>Peso:</strong> {solicitacao.peso.toLocaleString('pt-BR')} kg</div>
                      <div><strong>Volume:</strong> {solicitacao.volume} m³</div>
                      <div><strong>Conferente:</strong> {solicitacao.conferente}</div>
                      <div><strong>Tipo Produto:</strong> {solicitacao.tipo_produto}</div>
                      <div><strong>Tipo Carga:</strong> {solicitacao.tipo_carga}</div>
                    </div>
                    <div className="text-sm text-gray-600 mt-2">
                      <p><strong>Data da Aprovação:</strong> {formatarDataHora(solicitacao.data_atualizacao)}</p>
                    </div>
                    <div className="mt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleVisualizarDocumentos(solicitacao)}
                      >
                        Documentos
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Conteúdo da aba Reprovações */}
      {abaAtiva === 'reprovados' && (
        <div className="space-y-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4">Solicitações Reprovadas</h3>
            
            {/* Filtros */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fornecedor</label>
                <input
                  type="text"
                  placeholder="Buscar por fornecedor..."
                  value={fornecedorFiltro}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFornecedorFiltro(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data Inicial</label>
                <DatePicker
                  value={dataInicialFiltro}
                  onChange={(date) => setDataInicialFiltro(date ? date.toISOString().split('T')[0] : '')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data Final</label>
                <DatePicker
                  value={dataFinalFiltro}
                  onChange={(date) => setDataFinalFiltro(date ? date.toISOString().split('T')[0] : '')}
                />
              </div>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <p className="text-gray-500">Carregando solicitações...</p>
              </div>
            ) : filtrarHistorico(solicitacoes, StatusSolicitacao.REPROVADO).length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">Nenhuma reprovação encontrada</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filtrarHistorico(solicitacoes, StatusSolicitacao.REPROVADO).map((solicitacao) => (
                  <div 
                    key={solicitacao.id} 
                    className="bg-white rounded-lg shadow p-4 border-l-4 border-red-500"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-medium text-gray-900">{solicitacao.fornecedor}</h4>
                      <span className="text-sm text-gray-500">{solicitacao.solicitante_nome}</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm text-gray-600 mb-2">
                      <div><strong>Data Desejada:</strong> {formatarData(solicitacao.data_desejada)}</div>
                      <div><strong>Peso:</strong> {solicitacao.peso.toLocaleString('pt-BR')} kg</div>
                      <div><strong>Volume:</strong> {solicitacao.volume} m³</div>
                      <div><strong>Conferente:</strong> {solicitacao.conferente}</div>
                      <div><strong>Tipo Produto:</strong> {solicitacao.tipo_produto}</div>
                      <div><strong>Tipo Carga:</strong> {solicitacao.tipo_carga}</div>
                    </div>
                    {solicitacao.motivo_reprovacao && (
                      <div className="bg-red-50 p-3 rounded-md mt-2">
                        <p className="text-sm text-red-800"><strong>Motivo da Reprovação:</strong> {solicitacao.motivo_reprovacao}</p>
                      </div>
                    )}
                    <div className="text-sm text-gray-600 mt-2">
                      <p><strong>Data da Reprovação:</strong> {formatarDataHora(solicitacao.data_atualizacao)}</p>
                    </div>
                    <div className="mt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleVisualizarDocumentos(solicitacao)}
                      >
                        Documentos
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Conteúdo da aba Reagendamentos */}
      {abaAtiva === 'reagendados' && (
        <div className="space-y-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4">Solicitações Reagendadas</h3>
            
            {/* Filtros */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fornecedor</label>
                <input
                  type="text"
                  placeholder="Buscar por fornecedor..."
                  value={fornecedorFiltro}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFornecedorFiltro(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data Inicial</label>
                <DatePicker
                  value={dataInicialFiltro}
                  onChange={(date) => setDataInicialFiltro(date ? date.toISOString().split('T')[0] : '')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data Final</label>
                <DatePicker
                  value={dataFinalFiltro}
                  onChange={(date) => setDataFinalFiltro(date ? date.toISOString().split('T')[0] : '')}
                />
              </div>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <p className="text-gray-500">Carregando solicitações...</p>
              </div>
            ) : filtrarHistorico(solicitacoes, StatusSolicitacao.REAGENDADO).length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">Nenhum reagendamento encontrado</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filtrarHistorico(solicitacoes, StatusSolicitacao.REAGENDADO).map((solicitacao) => (
                  <div 
                    key={solicitacao.id} 
                    className="bg-white rounded-lg shadow p-4 border-l-4 border-orange-500"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-medium text-gray-900">{solicitacao.fornecedor}</h4>
                      <span className="text-sm text-gray-500">{solicitacao.solicitante_nome}</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm text-gray-600 mb-2">
                      <div><strong>Data Desejada:</strong> {formatarData(solicitacao.data_desejada)}</div>
                      <div><strong>Peso:</strong> {solicitacao.peso.toLocaleString('pt-BR')} kg</div>
                      <div><strong>Volume:</strong> {solicitacao.volume} m³</div>
                      <div><strong>Conferente:</strong> {solicitacao.conferente}</div>
                      <div><strong>Tipo Produto:</strong> {solicitacao.tipo_produto}</div>
                      <div><strong>Tipo Carga:</strong> {solicitacao.tipo_carga}</div>
                    </div>
                    {solicitacao.reagendamento_proposto && (
                      <div className="bg-orange-50 p-3 rounded-md mt-2">
                        <p className="text-sm text-orange-800"><strong>Nova Data:</strong> {formatarData(solicitacao.reagendamento_proposto.novaData)}</p>
                        <p className="text-sm text-orange-800"><strong>Motivo:</strong> {solicitacao.reagendamento_proposto.motivo}</p>
                        <p className="text-sm text-orange-800"><strong>Proposto por:</strong> {solicitacao.reagendamento_proposto.propostoPor}</p>
                      </div>
                    )}
                    <div className="text-sm text-gray-600 mt-2">
                      <p><strong>Data do Reagendamento:</strong> {formatarDataHora(solicitacao.data_atualizacao)}</p>
                    </div>
                    <div className="mt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleVisualizarDocumentos(solicitacao)}
                      >
                        Documentos
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Conteúdo da aba Todas as Solicitações */}
      {abaAtiva === 'todas' && (
        <div className="space-y-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4">Todas as Solicitações</h3>
            
            {/* Filtros */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fornecedor</label>
                <input
                  type="text"
                  placeholder="Buscar por fornecedor..."
                  value={fornecedorFiltro}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFornecedorFiltro(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data Inicial</label>
                <DatePicker
                  value={dataInicialFiltro}
                  onChange={(date) => setDataInicialFiltro(date ? date.toISOString().split('T')[0] : '')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data Final</label>
                <DatePicker
                  value={dataFinalFiltro}
                  onChange={(date) => setDataFinalFiltro(date ? date.toISOString().split('T')[0] : '')}
                />
              </div>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <p className="text-gray-500">Carregando solicitações...</p>
              </div>
            ) : filtrarHistorico(solicitacoes).length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">Nenhuma solicitação encontrada</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filtrarHistorico(solicitacoes).map((solicitacao) => (
                  <div 
                    key={solicitacao.id} 
                    className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-medium text-gray-900">{solicitacao.fornecedor}</h4>
                      <span className="text-sm text-gray-500">{solicitacao.status}</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm text-gray-600 mb-2">
                      <div><strong>Data Desejada:</strong> {formatarData(solicitacao.data_desejada)}</div>
                      <div><strong>Peso:</strong> {solicitacao.peso.toLocaleString('pt-BR')} kg</div>
                      <div><strong>Volume:</strong> {solicitacao.volume} m³</div>
                      <div><strong>Conferente:</strong> {solicitacao.conferente}</div>
                      <div><strong>Tipo Produto:</strong> {solicitacao.tipo_produto}</div>
                      <div><strong>Tipo Carga:</strong> {solicitacao.tipo_carga}</div>
                    </div>
                    <div className="text-sm text-gray-600 mt-2">
                      <p><strong>Data da Atualização:</strong> {formatarDataHora(solicitacao.data_atualizacao)}</p>
                    </div>
                    <div className="mt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleVisualizarDocumentos(solicitacao)}
                      >
                        Documentos
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de Reagendamento */}
      {modalReagendamento.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Propor Reagendamento</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nova Data</label>
                <Input
                  type="date"
                  value={formDataReagendamento.novaData}
                  onChange={(value) => setFormDataReagendamento(prev => ({ ...prev, novaData: value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Motivo</label>
                <textarea
                  value={formDataReagendamento.motivo}
                  onChange={(e) => setFormDataReagendamento(prev => ({ ...prev, motivo: e.target.value }))}
                  rows={3}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  placeholder="Informe o motivo do reagendamento"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-4">
              <Button
                variant="outline"
                onClick={() => setModalReagendamento({ open: false })}
              >
                Cancelar
              </Button>
              {podeReagendar && (
              <Button
                onClick={handleReagendar}
                loading={loading}
                disabled={loading}
              >
                Propor Reagendamento
              </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Documentos */}
      {modalDocumentos.open && modalDocumentos.solicitacao && (
        <DocumentViewer
          isOpen={modalDocumentos.open}
          onClose={() => setModalDocumentos({ open: false })}
          documents={modalDocumentos.documentos || modalDocumentos.solicitacao.documentos || []}
          title={`Documentos - ${modalDocumentos.solicitacao.fornecedor}`}
        />
      )}
    </div>
  );
};

const GestaoAgendamentoProtegido = () => (
  <AbaProtegida nomeAba="Gestão de Agendamento">
    <GestaoAgendamento />
  </AbaProtegida>
);

export default GestaoAgendamentoProtegido;
